import { getCultivarQueryOptions } from "@packages/api-client/hooks/cultivarController";
import type {
	CreateCultivarMutationRequest,
	UpdateCultivarMutationRequest,
} from "@packages/api-client/types/cultivarController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCreateCultivar } from "@/api/grass";
import { LoadFailedState } from "@/components/LoadFailedState";
import { PageHeader } from "@/components/PageHeader";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { CultivarForm } from "./-components/CultivarForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/cultivars/new",
)({
	// Cultivar management is desk work — online-only by design.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	validateSearch: (
		search: Record<string, unknown>,
	): { farm?: string; from?: string } => ({
		farm: typeof search.farm === "string" ? search.farm : undefined,
		from: typeof search.from === "string" ? search.from : undefined,
	}),
	component: NewCultivarPage,
});

function NewCultivarPage() {
	const { orgId } = Route.useParams();
	const { farm, from } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const source = useQuery(getCultivarQueryOptions(orgId, from));

	const createMutation = useCreateCultivar();

	const handleSubmit = (
		formData: CreateCultivarMutationRequest | UpdateCultivarMutationRequest,
	) =>
		createMutation
			.mutateAsync({
				organizationId: orgId,
				data: formData as CreateCultivarMutationRequest,
			})
			.then(() => {
				navigate({
					to: "/app/org/$orgId/grass-production/cultivars",
					params: { orgId },
					search: { farm },
				});
			});

	if (from && source.isPending) return <DashboardSkeleton />;
	if (from && (source.isError || !source.data)) {
		return <LoadFailedState onRetry={() => void source.refetch()} />;
	}

	return (
		<div className="flex flex-col gap-6">
			<PageHeader title={t("grass.cultivar.create.title")} showBackButton />
			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					<CultivarForm
						prefill={source.data}
						onSubmit={handleSubmit}
						isSubmitting={createMutation.isPending}
					/>
				</div>
			</div>
		</div>
	);
}
